import { memo, useMemo } from 'react'
import { ImgIcon, ListItem, classNames } from '@panwds/react-ui'
import { launchAppInstance, history, getFrameworkVar } from '../common/utils/Sparky'
import { ArrowRightIcon, EyeIcon, OpenNewBrowserIcon } from '@panwds/icons'
import { LABEL_VIEWING } from '../common/constants/LabelConstants'
import type { VFC, MouseEvent } from 'react'
import type { TsgAppInstances } from './store'

const preventDefault = (e: MouseEvent<HTMLElement>) => {
  e.preventDefault()
}

type AppsListProps = {
  tsgAppInstances: TsgAppInstances
  onClose: () => void
}
const AppsList: VFC<AppsListProps> = ({ tsgAppInstances, onClose }) => {
  const listItems = useMemo(() => {
    const tsg_id = tsgAppInstances.tsg.id
    return tsgAppInstances.map((instance) => {
      const { app_id, display_name, normalized_logo_url } = instance.app
      const { inProgress, canLaunch, tenant_id, url, isCurrent, addIconCls } = instance
      const text = <>{display_name} {inProgress && <small className='sparky-hub-app-extra-text sparky-hub-app-in-progress'>in progress</small>}</>
      const noLink = inProgress || !url
      const content = noLink ? text : (
        <a
          className='tenant-app-link'
          href={url}
          target='_blank'
          rel='noopener noreferrer'
          onClick={preventDefault}
        >
          <span className='cm-text-gray-100 cm-inline-block cm-w-full dark:cm-text-gray-100'>{text}</span>
          {isCurrent && <div className='cm-text-gray-300 dark:cm-text-gray-300 cm-text-xs'>
            <EyeIcon size='xs' className='cm-mb-[-2px]' /> {LABEL_VIEWING}
          </div>}
        </a>
      )
      const handleClick = isCurrent ? () => { // todo: use framework getDefaultSingleTenantRoute
        history.push(getFrameworkVar('default_route') || '/') // go to default route
        onClose()
      } : () => {
        if (instance.launch ? instance.launch(instance) !== false : launchAppInstance(instance)) {
          onClose()
        }
      }
      const IconLaunch = noLink || isCurrent ? undefined :
        canLaunch ? ArrowRightIcon : OpenNewBrowserIcon
      return (
        <ListItem
          key={tenant_id || app_id}
          iconLeft={<ImgIcon src={normalized_logo_url} data-app-id={app_id} addClassName={classNames('tenant-app-icon cm-h-8 cm-w-8 cm-my-[-1rem]', addIconCls)} alt=' ' />}
          iconRight={IconLaunch && <IconLaunch size='xs' className='tenant-app-launch cm-text-primary' />}
          addClassName={classNames('cm-px-3 cm-py-2 cm-text-base cm-bg-blue-steel-950 dark:cm-bg-gray-1050' /*, isCurrent && 'cm-border-0 cm-border-b cm-border-solid cm-border-gray-300 dark:cm-border-gray-950' */)}
          disabled={inProgress}
          onClick={inProgress ? preventDefault : handleClick}
          dataTestId={`tsg:${tsg_id}:app:${app_id}`}
          data-tsg-id={tsg_id}
          data-app-id={app_id}
          data-tenant-id={tenant_id}
        >
          {content}
        </ListItem>
      )
    })
  }, [tsgAppInstances, onClose])

  if (!listItems.length) {
    return (
      <div className='cm-text-center cm-p-4' data-testid={`tsg-apps:${tsgAppInstances.tsg.id}:empty`}>No available apps in this tenant</div>
    )
  }
  return ( // copied and simplified from panwds List
    <ul
      className='cm-m-0 cm-p-0 cm-h-full cm-overflow-auto tw-scrollbar-dark dark:tw-scrollbar-track-dark dark:tw-scrollbar-thumb-dark dark:tw-scrollbar-corner-dark focus:cm-outline-none cm-text-default dark:cm-text-gray-100'
      data-testid={`tsg-apps:${tsgAppInstances.tsg.id}`}
      data-tsg-id={tsgAppInstances.tsg.id}
    >
      {listItems}
    </ul>
  )
}

export default memo(AppsList)
